import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

const ServicesSection = () => {
  const [openService, setOpenService] = useState(null);

  const services = [
    {
      title: 'Business Setup & Advisory',
      description: 'Setting up a new venture involves far more than paperwork. We guide entrepreneurs and promoters through every stage — from selecting the right business structure to obtaining statutory registrations, establishing accounting systems, and ensuring ongoing compliance readiness.',
      items: [
        'Private Limited Company Registration',
        'Limited Liability Partnership (LLP) Registration',
        'One Person Company (OPC) Formation',
        'Partnership Firm Registration',
        'Proprietorship Firm Registration',
        'MSME / Udyam Registration',
        'GST Registration & Initial Compliance Setup',
        'Start-up India Registration',
        'Statutory Approvals & Licences'
      ]
    },
    {
      title: 'Audit & Assurance',
      description: 'Independent, objective assurance that your financial records reflect the true picture. We conduct the full spectrum of audits required under Indian statute and sector-specific regulations.',
      items: [
        'Under Companies Act, 2013: Financial Audit u/s 139 | Cost Audit u/s 148 | Secretarial Audit u/s 208 | Internal Audit u/s 138(1)',
        'Under Direct & Indirect Tax Laws: Tax Audit u/s 44AB (Income Tax Act) | GST Audit u/s 35(5) | Customs & Central Excise Audits | State VAT Audits',
        'Under Other Statutes: Concurrent Audit, Branch Audit & Stock Audit under Banking Regulation Act | Billing & Metering Audit under TRAI | Audit of Stock Brokers & Credit Rating Agencies (SEBI) | Forensic Audit | Financial Audit of Banks, Insurance Companies, LLPs, HUFs, Societies & Trusts'
      ]
    },
    {
      title: 'Taxation Services',
      description: 'Comprehensive direct and indirect tax services — compliance, planning, filing, and representation — for individuals, businesses, and NRIs.',
      items: [
        'Income Tax Return Filing (Individuals, Firms, Companies, Trusts)',
        'Corporate Tax Planning & Advisory',
        'TDS / TCS Compliance & Returns',
        'GST Registration, Returns & Reconciliation',
        'GST Audit & Annual Return (GSTR-9/9C)',
        'International Taxation & DTAA Advisory',
        'NRI Taxation & Repatriation',
        'Income Tax Assessments & Appeals',
        'Form 15CA / 15CB Certification',
        'Transfer Pricing Advisory'
      ]
    },
    {
      title: 'Corporate & Transaction Advisory',
      description: 'Strategic guidance for businesses navigating restructuring, mergers, acquisitions, and complex transactional decisions.',
      items: [
        'Mergers & Acquisitions Advisory',
        'Due Diligence (Financial & Tax)',
        'Business Valuation',
        'Corporate Restructuring',
        'FEMA & RBI Compliance',
        'Joint Venture Structuring',
        'Foreign Direct Investment (FDI) Advisory',
        'ROC Compliance & Secretarial Services',
        'MCA Annual Filings & Director KYC'
      ]
    },
    {
      title: 'Project Finance & Fund Raising',
      description: 'Supporting businesses in accessing growth capital through debt and equity routes, with preparation of all supporting documentation.',
      items: [
        'Project Report Preparation',
        'CMA Data & Financial Projections',
        'Bank Loan Documentation & Liaison',
        'Working Capital Assessment',
        'Private Equity & Venture Capital Advisory',
        'NBFC Compliance',
        'Startup Funding Advisory'
      ]
    },
    {
      title: 'Finance & Accounting',
      description: 'Reliable, accurate outsourced accounting and financial management to keep your operations running smoothly.',
      items: [
        'Bookkeeping & Accounts Maintenance',
        'MIS Reporting & Financial Dashboards',
        'Payroll Processing & Compliance',
        'Virtual CFO Services',
        'Accounts Payable / Receivable Management',
        'Financial Statement Preparation (Ind AS / IFRS)',
        'Cash Flow Management'
      ]
    },
    {
      title: 'Credit Risk & Due Diligence Services',
      description: 'A technology-driven field services platform supporting lending institutions — banks and NBFCs alike — across diverse domains and geographies throughout Rajasthan and Delhi NCR.',
      items: [
        'Personal Discussion (PDs)',
        'Liquid Income Profile (LIPs)',
        'Assessed Income Program (AIP)',
        'Risk Containment Unit (RCU)',
        'Credit Processing Agency (CPA)'
      ]
    }
  ];

  const toggleService = (index) => {
    setOpenService(openService === index ? null : index);
  };

  return (
    <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0D1B2A] via-[#1a2d42] to-[#0D1B2A]">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `repeating-linear-gradient(0deg, #C9A84C, #C9A84C 1px, transparent 1px, transparent 20px),
                           repeating-linear-gradient(90deg, #C9A84C, #C9A84C 1px, transparent 1px, transparent 20px)`
        }}></div>
      </div>
      
      <div className="max-w-6xl mx-auto relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-playfair font-bold text-[#C9A84C] mb-4">
            Our Services
          </h2>
          <div className="w-24 h-1 bg-[#C9A84C] mx-auto mb-6"></div>
          <p className="text-gray-300 font-dmsans max-w-3xl mx-auto">
            Comprehensive professional services tailored to your business needs.
          </p>
        </div>

        <div className="space-y-4">
          {services.map((service, index) => (
            <div
              key={index}
              className="bg-[#1a2d42] bg-opacity-50 rounded-lg shadow-md overflow-hidden border border-[#C9A84C] border-opacity-20 hover:border-[#C9A84C] transition-all duration-300"
            >
              <button
                onClick={() => toggleService(index)}
                className="w-full px-6 py-5 flex justify-between items-center text-left hover:bg-[#0D1B2A] hover:bg-opacity-50 transition-colors duration-300"
              >
                <div>
                  <h3 className="text-xl sm:text-2xl font-playfair font-bold text-[#C9A84C] mb-1">
                    {service.title}
                  </h3>
                  <p className="text-sm text-gray-400 font-dmsans">
                    {openService === index ? 'Click to collapse' : 'Click to expand'}
                  </p>
                </div>
                <div className="text-[#C9A84C]">
                  {openService === index ? <ChevronUp size={28} /> : <ChevronDown size={28} />}
                </div>
              </button>

              <div
                className={`transition-all duration-500 ease-in-out ${
                  openService === index ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="px-6 pb-6 pt-2">
                  <p className="text-gray-300 font-dmsans mb-4 leading-relaxed">
                    {service.description}
                  </p>
                  <ul className="space-y-2">
                    {service.items.map((item, itemIndex) => (
                      <li
                        key={itemIndex}
                        className="flex items-start text-gray-300 font-dmsans text-sm"
                      >
                        <span className="text-[#C9A84C] mr-3 mt-1 flex-shrink-0">▪</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
